import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';
import { getData } from '../utils/api';
import { ThemeOption } from '../types/enums';
import { LanguageType, ProductFlags } from '../@types';
import { Skin } from '../types/enums';
import {
  FEED_SOURCE,
  FeedSource,
  CompetitionsFlagVariant,
  LAYOUT_TYPE,
  LayoutType,
  MARKETPLACE_AUTH_MODEL,
  MarketplaceAuthModel,
  KycVerificationTrigger,
  OddsFormat,
  PAYMENT_GATEWAY,
  PaymentGateway,
  REOFFER_DISPLAY,
  ReofferDisplay,
  SportIconVariant,
} from '../types/enums';
import { hasCasinoOnlyProducts } from '../helpers/products';

export type DomainBranding = {
  domain: string;
  branding: {
    skin: Skin;
    theme: ThemeOption;
  };
};

type DerivedMarketplaceConfigData = {
  layoutType: LayoutType;
  isStandaloneCasino: boolean;
  isFTBFeed: boolean;
  isPay2LocalPayment: boolean;
  isUserRegistration: boolean;
  isHybrid: boolean;
  isAgentSystem: boolean;
  showReofferComparison: boolean;
};

export type EmailVerificationFlowType = 'onLogin' | 'onChangePassword' | 'none';

export interface SignUpFormField {
  name: string;
  order: number;
  required: boolean;
}

export type WidgetFeatures = {
  preMatchWidget: boolean;
  liveWidget: boolean;
};

export type MarketplaceConfig = {
  name: string;
  feedRunning: boolean;
  casinoRunning: boolean;
  id: string;
  sanityConfig: {
    projectId: string;
    dataset: string;
  };
  branding: ThemeOption;
  domains?: DomainBranding[];
  marketplaceConfig: {
    cryptoProvider?: 'moralis' | 'zero_x_processing';
    moralis?: { depositEnabled?: boolean; withdrawalEnabled?: boolean };
    zeroXProcessing?: { enabled?: boolean; depositEnabled?: boolean; withdrawalEnabled?: boolean };
    currency: string;
    paymentGateway: PaymentGateway;
    products: ProductFlags;
    documentTitle?: string;
    model: MarketplaceAuthModel;
    maintenance?: {
      underMaintenance?: boolean;
      message?: Record<string, string>;
    };
    registrationFlow: {
      emailVerification: EmailVerificationFlowType;
      signUpFormFields: SignUpFormField[];
    };
    languageConfig: {
      defaultLanguage: LanguageType;
      supportedLanguages: LanguageType[];
    };
    googleAnalyticsId?: string;
    openReplayProjectKey?: string | null;
    kyc?: {
      enabled: boolean;
      required?: boolean;
      trigger?: KycVerificationTrigger;
    };
  };
  sportsbookConfig: {
    oddsFormat: OddsFormat;
    feedSource: FeedSource;
    betBuilder?: boolean;
    reofferOddsDisplay?: ReofferDisplay;
    widgetFeatures?: WidgetFeatures;
    assetVariants?: {
      sportIcons?: SportIconVariant;
      competitionsFlags?: CompetitionsFlagVariant;
      teamAssets?: 'none' | 'logo' | 'jersey';
    };
  };
  casinoConfig: {
    layoutTemplate: string;
    jackpotEnabled?: boolean;
  };
};

type UseMarketplaceConfigOptions = UseQueryOptions<MarketplaceConfig, Error>;

export const useMarketplaceConfig = (
  options?: UseMarketplaceConfigOptions
): UseQueryResult<MarketplaceConfig> & DerivedMarketplaceConfigData => {
  const queryData = useQuery({
    queryKey: [QUERY_KEYS.marketplaceConfig],
    queryFn: async (): Promise<MarketplaceConfig> => await getData('marketplace/config', undefined, 'crm'),
    ...options,
  });

  const isStandaloneCasino = queryData.isSuccess && hasCasinoOnlyProducts(queryData.data?.marketplaceConfig.products);

  const layoutType = isStandaloneCasino ? LAYOUT_TYPE.CASINO : LAYOUT_TYPE.SPORTSBOOK;

  const feedSource = queryData.data?.sportsbookConfig.feedSource;
  const isFTBFeed = feedSource === FEED_SOURCE.FEED_THE_BET;
  const isPay2LocalPayment = queryData?.data?.marketplaceConfig.paymentGateway === PAYMENT_GATEWAY.PAY_2_LOCAL;

  const reofferOddsDisplay = queryData.data?.sportsbookConfig.reofferOddsDisplay ?? REOFFER_DISPLAY.ALL;
  const showReofferComparison = reofferOddsDisplay === REOFFER_DISPLAY.ALL;

  const authModel = queryData.data?.marketplaceConfig.model;

  const authModelFlags = {
    isUserRegistration: authModel === MARKETPLACE_AUTH_MODEL.USER_REGISTRATION,
    isHybrid: authModel === MARKETPLACE_AUTH_MODEL.HYBRID,
    isAgentSystem: authModel === MARKETPLACE_AUTH_MODEL.AGENT_SYSTEM,
  };

  return {
    ...queryData,
    layoutType,
    isStandaloneCasino,
    isFTBFeed,
    isPay2LocalPayment,
    showReofferComparison,
    ...authModelFlags,
  };
};
